import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { SOCIAL_META_KEYS, SOCIAL_META_DEFAULTS, resolveSocialMeta, type SocialMeta } from "@/lib/social-meta";

const saveSchema = z.object({
  title: z.string().trim().min(5).max(120),
  description: z.string().trim().min(20).max(300),
});

/** Public: current social preview text, used to inject <head> meta during SSR. */
export const getSocialMeta = createServerFn({ method: "GET" }).handler(async (): Promise<SocialMeta> => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
  const url = process.env["SUPABASE_URL"];
  if (!key || !url) return SOCIAL_META_DEFAULTS;

  try {
    const supabasePublic = createClient(url, key, {
      auth: { persistSession: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data } = await supabasePublic
      .from("site_content")
      .select("key, value")
      .in("key", [SOCIAL_META_KEYS.title, SOCIAL_META_KEYS.description]);
    const overrides: Record<string, string> = {};
    for (const row of (data ?? []) as { key: string; value: string }[]) overrides[row.key] = row.value;
    return resolveSocialMeta(overrides);
  } catch {
    return SOCIAL_META_DEFAULTS;
  }
});

export const saveSocialMeta = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => saveSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Response("Forbidden", { status: 403 });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();
    const { error } = await supabaseAdmin.from("site_content").upsert(
      [
        { key: SOCIAL_META_KEYS.title, value: data.title, updated_by: context.userId, updated_at: now },
        { key: SOCIAL_META_KEYS.description, value: data.description, updated_by: context.userId, updated_at: now },
      ],
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
